import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from 'js/components/Button'
import Flexer from 'js/components/Flexer/Flexer'
import { Row } from 'js/components/Grid'
import Modal from 'js/components/Modal/Modal'

import Option from './Option/Option'

type Props = {
    isMembershipOnRequest: boolean | null
    onChangeIsMembershipOnRequest: (value: boolean) => void
    isClosed: boolean | null
    onChangeIsClosed: (value: boolean) => void
}

const GroupTypePicker = ({
    isMembershipOnRequest,
    onChangeIsMembershipOnRequest,
    isClosed,
    onChangeIsClosed,
}: Props) => {
    const { t } = useTranslation()

    const [showAccessWarningModal, setShowAccessWarningModal] = useState(false)

    const toggleAccessWarningModal = () => {
        setShowAccessWarningModal(!showAccessWarningModal)
    }

    const confirmAccessWarning = () => {
        setOption(2)
        toggleAccessWarningModal()
    }

    const setOption = (groupType: number) => {
        let isMembershipOnRequest: boolean, isClosed: boolean

        switch (groupType) {
            case 0:
                isMembershipOnRequest = false
                isClosed = false
                break
            case 1:
                isMembershipOnRequest = true
                isClosed = false
                break
            case 2:
                isMembershipOnRequest = true
                isClosed = true
                break
        }

        onChangeIsMembershipOnRequest(isMembershipOnRequest)
        onChangeIsClosed(isClosed)
    }

    const getGroupType = (): number | undefined => {
        if (isMembershipOnRequest !== null && isClosed !== null) {
            let groupType: number
            if (!isMembershipOnRequest && !isClosed) {
                groupType = 0
            } else if (isMembershipOnRequest && !isClosed) {
                groupType = 1
            } else if (isMembershipOnRequest && isClosed) {
                groupType = 2
            }
            return groupType
        }
        return undefined
    }

    const groupType = getGroupType()

    return (
        <>
            <Row $growContent $gutter={0}>
                <Option
                    title={t('entity-group.type-open')}
                    value={0}
                    features={
                        t('entity-group.type-open-features', {
                            returnObjects: true,
                        }) as any
                    }
                    checked={groupType === 0}
                    setOption={setOption}
                />
                <Option
                    title={t('entity-group.type-semi-open')}
                    value={1}
                    features={
                        t('entity-group.type-semi-open-features', {
                            returnObjects: true,
                        }) as any
                    }
                    checked={groupType === 1}
                    setOption={setOption}
                />
                <Option
                    title={t('entity-group.type-private')}
                    value={2}
                    features={
                        t('entity-group.type-private-features', {
                            returnObjects: true,
                        }) as any
                    }
                    checked={groupType === 2}
                    setOption={
                        isClosed !== undefined // If changing type of an existing group to closed
                            ? toggleAccessWarningModal
                            : setOption
                    }
                />
            </Row>
            <Modal
                isVisible={showAccessWarningModal}
                title={t('form.access-level')}
                size="small"
                onClose={toggleAccessWarningModal}
            >
                {t('entity-group.type-private-warning')}
                <Flexer mt>
                    <Button
                        size="normal"
                        variant="tertiary"
                        type="button"
                        onClick={toggleAccessWarningModal}
                    >
                        {t('action.no-back')}
                    </Button>
                    <Button
                        size="normal"
                        variant="primary"
                        onClick={confirmAccessWarning}
                    >
                        {t('action.yes-confirm')}
                    </Button>
                </Flexer>
            </Modal>
        </>
    )
}

export default GroupTypePicker
